import { Button, Card, Flex, Grid, Text, TextField } from '@radix-ui/themes'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { SectionCard } from '~/components/SectionCard'
import { getSite } from '~/features/sites/service'
import { LuEuro } from 'react-icons/lu'
import { ArrowRightIcon } from '@radix-ui/react-icons'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { updateWaterStation } from '~/features/stations/service'
import { HistoricalInvoicesSection } from '~/features/budget/components/HistoricalInvoicesSection'
import { BudgetGoalList } from '~/features/budget/components/BudgetGoalList'
import { MdOutlineWifiTethering } from 'react-icons/md'
import LinkButton from '~/components/LinkButton'
import { EmptyState } from '~/components/EmptyState'

const SiteBudgetPage = () => {
  const { t } = useTranslation('sites')
  const { siteID } = Route.useParams()
  const { queryClient } = Route.useRouteContext()

  const { data: site } = useSuspenseQuery({
    queryFn: () => getSite({ siteId: siteID }),
    queryKey: ['site', siteID]
  })

  const [costAdjustedStations, setUpdatedStations] = useState(site.stations)

  const { mutate: updateStationsCost, isPending } = useMutation({
    mutationFn: async () => {
      for (const station of costAdjustedStations) {
        const originalStation = site.stations.find((s) => s.id === station.id)
        if (!originalStation || originalStation.cost === station.cost) {
          continue
        }
        await updateWaterStation(station)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          // TODO invalidates all other queries for stations and budget items once implemented
          const isSiteQuery = `${queryKey[0]}`.includes('site') && queryKey[1] === siteID
          const isStationsQuery = queryKey[0] === 'stations' || queryKey[0] === 'station'

          return isSiteQuery || isStationsQuery
        }
      })
      toast.success(t('budget.messages.updateSuccess'))
    },
    onError: (error) => {
      console.error('Error updating site', error)
      toast.error(t('budget.messages.updateError'))
    }
  })

  const hasChanges = useMemo(
    () =>
      site.stations.some(
        (initialStation) =>
          costAdjustedStations.find((s) => s.id === initialStation.id)?.cost !== initialStation.cost
      ),
    [costAdjustedStations, site]
  )

  return (
    <Flex direction="column" gap="4">
      <SectionCard title={t('budget.costSection.title')}>
        <Text size="2" color="gray">
          {t('budget.costSection.description')}
        </Text>
        {site.stations.length === 0 ? (
          <Card size={'4'}>
            <EmptyState
              title={t('budget.stationsSection.emptyState.title')}
              description={t('budget.stationsSection.emptyState.description')}
              afterBody={
                <LinkButton
                  variant="soft"
                  to={'/sites/$siteID/config/stations/new'}
                  params={{ siteID }}
                  text={t('budget.stationsSection.emptyState.addStation')}
                />
              }
            />
          </Card>
        ) : (
          <>
            <Grid pb={'4'} columns={'2'} gap={'2'} className="border-[1px] border-gray-4 rounded-md">
              <Grid className="p-2 col-[1/-1] grid-cols-subgrid bg-gray-2 dark:bg-gray-3  text-center font-medium">
                <Flex align={'center'} gap={'1'} m="auto">
                  <MdOutlineWifiTethering className="text-gray-10" />
                  <Text size={'2'}>&nbsp;{t('budget.stationsSection.title')}</Text>
                </Flex>
                <Text size={'2'}>{t('budget.costSection.costLabel')}</Text>
              </Grid>
              {_(costAdjustedStations)
                .orderBy('name')
                .value()
                .map((station) => (
                  <Grid key={station.id} className={'px-4 h-10 col-[1/-1] grid-cols-subgrid text-center'}>
                    <LinkButton
                      color="gray"
                      className="m-auto group pl-4"
                      to="/sites/$siteID/config/stations/$stationID"
                      params={{ siteID, stationID: station.id }}
                      text={station.name}
                      trailingIcon={
                        <ArrowRightIcon className="group-hover:opacity-100 opacity-0 transition-all duration-400 ease-in-out transform group-hover:translate-x-1" />
                      }
                    />
                    <TextField.Root
                      type="number"
                      min={0}
                      value={station.cost ?? 0}
                      onChange={(e) => {
                        const newCost = e.target.valueAsNumber
                        setUpdatedStations((prev) =>
                          prev.map((s) => (s.id === station.id ? { ...s, cost: newCost } : s))
                        )
                      }}
                      placeholder={t('budget.costSection.stationCostPlaceholder')}
                      size="2"
                      className="w-fit m-auto"
                    >
                      <TextField.Slot className="text-gray-10" side="left">
                        <LuEuro />
                      </TextField.Slot>
                    </TextField.Root>
                  </Grid>
                ))}
            </Grid>
            <Button
              mt="5"
              disabled={!hasChanges}
              size={'3'}
              loading={isPending}
              onClick={() => updateStationsCost()}
              className="ml-auto"
            >
              {t('budget.updateButton')}
            </Button>
          </>
        )}
      </SectionCard>
      <SectionCard title={t('budget.invoiceSection.title')}>
        <HistoricalInvoicesSection siteID={siteID} />
      </SectionCard>
      <SectionCard title={t('budget.objectives.title')}>
        <Flex direction={'column'} gap="6">
          <Text size="2" color="gray">
            {t('budget.objectives.description')}
          </Text>
          <BudgetGoalList siteID={siteID} />
        </Flex>
      </SectionCard>
    </Flex>
  )
}

export const Route = createFileRoute('/sites/$siteID/config/budget/')({
  component: SiteBudgetPage
})
